import React, { useMemo } from 'react';
import {
  Area,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Line,
  ComposedChart,
  XAxis,
  Label
} from 'recharts';
import { CalendarDateService, DateObject } from '@uplift/services';
import { useL10n } from '@uplift/hooks';
import { useTheme } from '@mui/material';


export type SmallProgressChartProps = {
  aspectRatio?: number;
  fillUpTo?: DateObject;
  maxScore?: number | null;
  data: {
    datetime: DateObject;
    score: number;
    scoreDiffPercentage?: number;
  }[];
};


export function SmallProgressChart(props: SmallProgressChartProps) {
  const l10n = useL10n();
  const theme = useTheme();
  const range = React.useMemo(() => {
    const sortedData = [ ...props.data ].sort((a, b) => a.datetime.isSameOrAfter(b.datetime) ? 1 : -1);
    const transformedData = sortedData.map(el => {
      const timestamp = el.datetime.unix();

      return {
        timestamp: timestamp,
        score: el.score,
      };
    });
    const oldest = sortedData[0];
    const newest = sortedData[sortedData.length - 1];
    const scores = sortedData.map(d => d.score);
    const minScore = Math.min(...scores);
    const maxScore = Math.max(...scores);

    return {
      data: transformedData,
      oldest,
      newest,
      minScore,
      maxScore,
    };
  }, [ props.data ]);

  const fillUpData = React.useMemo(() => {
    if (props.fillUpTo) {
      const last = range.data[range.data.length - 1];

      if (last.timestamp > props.fillUpTo.unix()) {
        return [];
      }

      return [
        {
          timestamp: last.timestamp,
          score: range.newest.score,
          scoreFill: range.newest.score,
        },
        {
          timestamp: props.fillUpTo.unix(),
          scoreFill: range.newest.score,
        },
      ];
    }

    return [];
  }, [ props.fillUpTo, range ]);

  const maxScore = useMemo(() => {
    return (props?.maxScore || range.maxScore);
  }, [ props?.maxScore, range.maxScore ]);

  const maxTicksCount = useMemo(() => {
    if (maxScore % 2 === 1) {
      return 4;
    }
    return 5;
  }, [ maxScore ]);

  const xAxisProps = React.useMemo(() => {
    const newest = props.fillUpTo ?? range.newest.datetime;

    // if 6 month
    if (newest.diff(range.oldest.datetime, 'weeks') >= 24) {
      return {
        label: l10n.shared.charts.ProgressChart.last6Months,
        ticks: [
          newest.subtract(5, 'month').unix(),
          newest.subtract(4, 'month').unix(),
          newest.subtract(3, 'month').unix(),
          newest.subtract(2, 'month').unix(),
          newest.subtract(1, 'month').unix(),
          newest.unix(),
        ],
        tickFormatter: (v: string) => CalendarDateService.date.unix(parseInt(v)).format('MMM')
      };
    }

    const fullRange = newest.unix() - range.oldest.datetime.unix();
    const start = range.oldest.datetime.unix();
    const end = newest.unix();
    const weeksBetween = newest.diff(range.oldest.datetime, 'weeks');
    const isEvenWeeks = weeksBetween % 2 === 0;
    const ticks = isEvenWeeks
      ? [
        Math.round(range.oldest.datetime.unix() + fullRange / 3),
        Math.round(range.oldest.datetime.unix() + fullRange / 3 * 2)
      ]
      : [
        Math.round(range.oldest.datetime.unix() + fullRange / 2)
      ];

    return {
      label: l10n.shared.charts.ProgressChart.weeks,
      ticks: range.data.length > 4
        ? [ start, ...ticks, end ]
        : [ start, end ],
      tickFormatter: (val: string) => {
        const value = parseInt(val, 10);

        return (CalendarDateService.date.unix(value).diff(range.oldest.datetime, 'weeks') + 1).toString();
      }
    };
  }, [ range, props.fillUpTo, l10n ]);

  const yTicks = React.useMemo(() => {
    // there should be 4 ticks on the chart
    const ticks = maxTicksCount === 4
      ? [
        0,
        Math.round(maxScore / 3),
        Math.round(maxScore / 3 * 2)
      ]
      : [
        0,
        Math.round(maxScore / 4),
        Math.round(maxScore / 4 * 2),
        Math.round(maxScore / 4 * 3),
      ];

    ticks.push(props?.maxScore || range.maxScore);

    return ticks;

  }, [ maxScore, maxTicksCount, props?.maxScore, range.maxScore ]);

  const allData = React.useMemo(() => [
    ...range.data,
    ...fillUpData
  ], [ range.data, fillUpData ]);

  return (
    <ResponsiveContainer debounce={100} aspect={props.aspectRatio ?? (2 / 1)}>
      <ComposedChart
        data={allData}
        style={{ fontSize: 14 }}
        margin={{
          top: 4,
          left: -36,
          right: 0,
          bottom: 12,
        }}
      >
        <CartesianGrid
          vertical={false}
          strokeLinecap="round"
          strokeDasharray="0 3"
        />
        <XAxis
          dataKey="timestamp"
          axisLine={false}
          tickLine={false}
          type="number"
          allowDataOverflow
          domain={[ xAxisProps.ticks[0], 'maxData' ]}
          ticks={xAxisProps.ticks}
          tickFormatter={xAxisProps.tickFormatter}
        >
          <Label offset={28} value={xAxisProps.label} position="insideTop" />
        </XAxis>
        <YAxis
          dataKey="score"
          axisLine={false}
          tickLine={false}
          tickCount={maxTicksCount}
          ticks={yTicks}
          type="number"
        />
        <defs>
          <linearGradient id="colorPurple" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.2}/>
            <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0}/>
          </linearGradient>
        </defs>
        <Line
          dot={false}
          type="linear"
          dataKey="scoreFill"
          stroke="rgba(0,0,0,0.26)"
          strokeLinecap="square"
          strokeDasharray="2 5"
          strokeWidth="2px"
          isAnimationActive={false}
        />
        <Area
          type="linear"
          dataKey="score"
          stroke={theme.palette.primary.main}
          strokeWidth="2px"
          fill="url(#colorPurple)"
          isAnimationActive={false}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
}
